import type { MessageReference, UploadFile } from '@liniya/api-client';
import { create } from 'zustand';

import { releasePreview } from '../platform';

export interface ComposerAttachment {
  file: UploadFile;
  /**
   * Carried separately: a React Native file descriptor is a plain {uri, name,
   * type} object and has neither `name` nor `size` the way a `File` does.
   */
  name: string;
  size: number;
  previewUrl: string;
  kind: 'image' | 'video' | 'audio' | 'document';
}

interface ComposerState {
  /** One draft per chat, so switching conversations never loses typing. */
  drafts: Record<string, string>;
  references: Record<string, MessageReference | null>;
  attachments: Record<string, ComposerAttachment | null>;
  editing: Record<string, { messageId: string; original: string } | null>;

  setDraft: (chatId: string, text: string) => void;
  draft: (chatId: string) => string;
  setReference: (chatId: string, reference: MessageReference | null) => void;
  setAttachment: (chatId: string, attachment: ComposerAttachment | null) => void;
  startEditing: (chatId: string, messageId: string, original: string) => void;
  stopEditing: (chatId: string) => void;
  clearComposer: (chatId: string) => void;
}

export const useComposerStore = create<ComposerState>((set, get) => ({
  drafts: {},
  references: {},
  attachments: {},
  editing: {},

  setDraft: (chatId, text) => set((state) => ({ drafts: { ...state.drafts, [chatId]: text } })),
  draft: (chatId) => get().drafts[chatId] ?? '',

  setReference: (chatId, reference) =>
    set((state) => ({ references: { ...state.references, [chatId]: reference } })),

  setAttachment: (chatId, attachment) => {
    const previous = get().attachments[chatId];
    // A browser object URL is a real allocation, not a string: dropping one
    // without releasing it keeps the whole file alive for the life of the
    // document. A phone hands back a file URI, which owns nothing.
    if (previous && previous.previewUrl !== attachment?.previewUrl) {
      releasePreview(previous.previewUrl);
    }
    set((state) => ({ attachments: { ...state.attachments, [chatId]: attachment } }));
  },

  startEditing: (chatId, messageId, original) =>
    set((state) => ({
      editing: { ...state.editing, [chatId]: { messageId, original } },
      drafts: { ...state.drafts, [chatId]: original },
      references: { ...state.references, [chatId]: null },
    })),

  stopEditing: (chatId) =>
    set((state) => ({
      editing: { ...state.editing, [chatId]: null },
      drafts: { ...state.drafts, [chatId]: '' },
    })),

  clearComposer: (chatId) => {
    const previous = get().attachments[chatId];
    if (previous) releasePreview(previous.previewUrl);
    set((state) => ({
      drafts: { ...state.drafts, [chatId]: '' },
      references: { ...state.references, [chatId]: null },
      attachments: { ...state.attachments, [chatId]: null },
      editing: { ...state.editing, [chatId]: null },
    }));
  },
}));
